"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import {
  PiWrench,
  PiSliders,
  PiMedal,
  PiRocket,
  PiCoins,
  PiPresentationChart,
  PiArrowRightBold,
  PiBookOpen,
  PiGraduationCap,
  PiChalkboardTeacher,
  PiCertificate,
  PiGlobe,
  PiCalculator,
  PiFlask,
  PiNotebook,
  PiBriefcase,
  PiLaptop,
  PiAtom,
  PiLightbulb,
  PiTrophy,
  PiPuzzlePiece,
  PiChats,
  PiClock,
  PiMapPin,
  PiCompass,
  PiSparkle,
} from "react-icons/pi";

const FEATURE_ICONS: Record<string, React.ElementType> = {
  wrench: PiWrench,
  sliders: PiSliders,
  medal: PiMedal,
  rocket: PiRocket,
  coins: PiCoins,
  chart: PiPresentationChart,
  book: PiBookOpen,
  graduation: PiGraduationCap,
  teacher: PiChalkboardTeacher,
  certificate: PiCertificate,
  globe: PiGlobe,
  calculator: PiCalculator,
  flask: PiFlask,
  notebook: PiNotebook,
  briefcase: PiBriefcase,
  laptop: PiLaptop,
  atom: PiAtom,
  lightbulb: PiLightbulb,
  trophy: PiTrophy,
  puzzle: PiPuzzlePiece,
  chats: PiChats,
  clock: PiClock,
  mappin: PiMapPin,
  compass: PiCompass,
  sparkles: PiSparkle,
};

interface FeatureItem {
  title: string;
  description: string;
  icon: string;
  image: string;
  readMoreLink?: string;
  hideReadMore?: boolean;
}

const renderIcon = (iconName: string) => {
  if (!iconName) return <PiBookOpen className="text-2xl rotate-45" />;

  const cleanIcon = iconName.trim();

  // Check if it's an SVG string
  if (cleanIcon.startsWith("<svg")) {
    return (
      <div 
        className="text-2xl rotate-45 flex items-center justify-center size-7 [&>svg]:size-full [&>svg]:fill-current"
        dangerouslySetInnerHTML={{ __html: cleanIcon }} 
      />
    );
  }

  // Check if it's an image URL
  if (cleanIcon.startsWith("/") || cleanIcon.startsWith("http://") || cleanIcon.startsWith("https://")) {
    return <img src={cleanIcon} alt="icon" className="size-7 object-contain rotate-45" />;
  }

  // Check if it's in our list of standard icons
  const IconComponent = FEATURE_ICONS[cleanIcon];
  if (IconComponent) {
    return <IconComponent className="text-2xl rotate-45" />;
  }

  // If it's something else (e.g. an emoji or custom text), just render it as text
  return <span className="text-2xl rotate-45 flex items-center justify-center select-none">{cleanIcon}</span>;
};

export default function OvanFeatures({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => window.removeEventListener("ovan-theme-change", handleThemeChange);
    }
  }, []);

  const defaultItems: FeatureItem[] = [
    {
      title: "Customer Focus",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "wrench",
      image: "/assets/service_1_1.jpg",
    },
    {
      title: "Data Analytics",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "sliders",
      image: "/assets/service_1_2.jpg",
    },
    {
      title: "Agile Management",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "medal",
      image: "/assets/service_1_3.jpg",
    },
    {
      title: "Market Analysis",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "rocket",
      image: "/assets/service_1_4.jpg",
    },
    {
      title: "Payroll & Accounting",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "coins",
      image: "/assets/service_1_5.jpg",
    },
    {
      title: "Sustainable Practice",
      description: "The phrasal sequence of the is now so that many campaign and benefit",
      icon: "chart",
      image: "/assets/service_1_6.jpg",
    },
  ];

  const items: FeatureItem[] = content.items || defaultItems;

  const updateItem = (idx: number, field: keyof FeatureItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  return (
    <section
      id="features"
      className={`relative py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-white"
      } ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""}`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center pb-12 max-w-xl mx-auto">
          <h2 className={`mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            <EditableText
              value={content.title || "Our Main Features"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, title: val })
              }
              as="span"
              className={isDark ? "text-white" : "text-slate-900"}
              placeholder="Features Title"
            />
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            <EditableText
              value={
                content.text ||
                "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
              }
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, text: val })
              }
              as="span"
              placeholder="Features Subtitle"
            />
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          {items.map((item, idx) => {
            return (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl border border-slate-800/40 cursor-pointer h-52 w-full"
              >
                {/* ── Default state (always visible, fades out on hover) ── */}
                <div
                  className={`absolute inset-0 w-full h-full z-10 flex p-6 transition-opacity duration-300 group-hover:opacity-0 ${
                    isDark ? "bg-slate-800/50" : "bg-slate-50"
                  }`}
                >
                  {/* Rotated icon circle */}
                  <div className="size-16 min-w-16 h-16 bg-blue-600/10 text-blue-500 rounded-full flex items-center justify-center -rotate-45 mt-1">
                    {renderIcon(item.icon)}
                  </div>
                  <div className="ms-5 flex flex-col justify-center">
                    <div className={`text-lg font-semibold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
                      <EditableText
                        value={item.title}
                        onChange={(val) => updateItem(idx, "title", val)}
                        as="span"
                        className={isDark ? "text-white" : "text-slate-900"}
                        placeholder="Feature Title"
                      />
                    </div>
                    <p className="text-slate-400 text-sm mb-3 line-clamp-2">
                      <EditableText
                        value={item.description}
                        onChange={(val) => updateItem(idx, "description", val)}
                        as="span"
                        placeholder="Feature Description"
                      />
                    </p>
                    {!item.hideReadMore && (
                      <span className="inline-flex items-center text-sm font-medium text-blue-500">
                        Read More <PiArrowRightBold className="ms-1 size-3" />
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Hover state: image + overlay (invisible by default, fades in) ── */}
                <div className="absolute inset-0 w-full h-full z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {/* Background image */}
                  <img
                    src={item.image || `/assets/service_1_${idx + 1}.jpg`}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-300"
                  />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-slate-900/75" />
                  {/* Text content over image */}
                  <div className="relative z-10 flex flex-col justify-center h-full p-7">
                    <h3 className="text-white text-xl font-bold mb-3 leading-snug">
                      <span className="text-white">
                        {item.title}
                      </span>
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-4">
                      <span>
                        {item.description}
                      </span>
                    </p>
                    {!item.hideReadMore && (
                      <a
                        href={item.readMoreLink || "#"}
                        className="inline-flex items-center text-sm font-semibold text-white gap-1 hover:gap-2 transition-all"
                      >
                        Read More <PiArrowRightBold className="size-3.5" />
                      </a>
                    )}
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
